/**
 * Contact Service
 *
 * Manages all contact-related operations:
 * - Creating/accepting contact requests
 * - Blocking/unblocking users
 * - Archiving contacts
 * - Validation (can user DM this person?)
 */

import { db } from '../config/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';

/**
 * Send a contact request
 * @param {string} fromUserId - User sending request
 * @param {string} toUserId - User receiving request
 * @param {string} source - How contact was found: 'name_search' | 'email_search' | 'phone_search' | 'code' | 'meeting'
 * @param {string} reason - Additional context (optional)
 * @returns {Promise<string>} requestId
 */
export async function sendContactRequest(fromUserId, toUserId, source = 'contact_request', reason = '') {
  try {
    // Check if already contacts
    const existing = await getContact(fromUserId, toUserId);
    if (existing && existing.status === 'accepted') {
      throw new Error('Already contacts');
    }

    // Check if there's already a pending request (either direction)
    const existingRequest = await getPendingRequestBetweenUsers(fromUserId, toUserId);
    if (existingRequest) {
      throw new Error('A pending request already exists between you and this user');
    }

    // Rate limit: max 10 requests per hour
    const recentRequests = await getRecentOutgoingRequests(fromUserId, 60); // last 60 minutes
    if (recentRequests.length >= 10) {
      throw new Error('Too many contact requests. Please try again later.');
    }

    const fromUser = await getDoc(doc(db, 'users', fromUserId));
    const fromUserData = fromUser.data();

    // Use deterministic ID to prevent duplicates: fromUserId_toUserId
    const requestId = `${fromUserId}_${toUserId}`;

    await setDoc(doc(db, 'contactRequests', requestId), {
      fromUserId,
      toUserId,
      source, // How this contact was discovered (name_search, email_search, code, etc.)
      reason,
      message: '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(), // iOS compatibility
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
      status: 'pending',
      fromUserName: fromUserData?.displayName || fromUserData?.name || 'Anonymous',
      fromUserEmail: fromUserData?.email || '', // iOS compatibility
      fromUserPhotoURL: fromUserData?.photoURL || null
    });

    return requestId;
  } catch (error) {
    console.error('Error sending contact request:', error);
    throw error;
  }
}

/**
 * Accept a contact request (creates BOTH contact documents bidirectionally)
 * @param {string} requestId
 */
export async function acceptContactRequest(requestId) {
  try {
    const requestDoc = await getDoc(doc(db, 'contactRequests', requestId));
    if (!requestDoc.exists()) throw new Error('Request not found');

    const requestData = requestDoc.data();
    const { fromUserId, toUserId, source } = requestData;

    // Get both user profiles
    const fromUser = await getDoc(doc(db, 'users', fromUserId));
    const fromUserData = fromUser.data();
    const toUser = await getDoc(doc(db, 'users', toUserId));
    const toUserData = toUser.data();

    const batch = writeBatch(db);

    // Create contact for recipient (toUser's contact entry for fromUser)
    batch.set(doc(db, 'contacts', `${toUserId}_${fromUserId}`), {
      userId: toUserId,
      contactId: fromUserId,
      status: 'accepted',
      source: source || 'contact_request',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      contactName: fromUserData?.displayName || fromUserData?.name || 'Anonymous',
      contactPhotoURL: fromUserData?.photoURL || null,
      email: fromUserData?.email || '', // iOS compatibility
      sharedMeetingsCount: 0,
      nickname: '',
      notes: ''
    });

    // Create contact for requester (fromUser's contact entry for toUser)
    // BIDIRECTIONAL: Both contacts created atomically
    batch.set(doc(db, 'contacts', `${fromUserId}_${toUserId}`), {
      userId: fromUserId,
      contactId: toUserId,
      status: 'accepted',
      source: source || 'contact_request',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      contactName: toUserData?.displayName || toUserData?.name || 'Anonymous',
      contactPhotoURL: toUserData?.photoURL || null,
      email: toUserData?.email || '', // iOS compatibility
      sharedMeetingsCount: 0,
      nickname: '',
      notes: ''
    });

    // Mark request as accepted
    batch.update(doc(db, 'contactRequests', requestId), {
      status: 'accepted',
      updatedAt: serverTimestamp()
    });

    await batch.commit();
  } catch (error) {
    console.error('Error accepting contact request:', error);
    throw error;
  }
}

/**
 * Requester creates their contact document when request is accepted
 * Called automatically when requester sees their request was accepted
 * @param {string} requestId
 */
export async function createRequesterContact(requestId) {
  try {
    const requestDoc = await getDoc(doc(db, 'contactRequests', requestId));
    if (!requestDoc.exists()) throw new Error('Request not found');

    const requestData = requestDoc.data();
    const { fromUserId, toUserId, source } = requestData;

    // Check if requester's contact already exists
    const existingContact = await getContact(fromUserId, toUserId);
    if (existingContact) {
      return; // Already created
    }

    const toUser = await getDoc(doc(db, 'users', toUserId));
    const toUserData = toUser.data();

    // Requester creates their own contact entry
    await setDoc(doc(db, 'contacts', `${fromUserId}_${toUserId}`), {
      userId: fromUserId,
      contactId: toUserId,
      status: 'accepted',
      source: source || 'contact_request',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      contactName: toUserData?.displayName || toUserData?.name || 'Anonymous',
      contactPhotoURL: toUserData?.photoURL || null,
      email: toUserData?.email || '', // iOS compatibility
      sharedMeetingsCount: 0,
      nickname: '',
      notes: ''
    });
  } catch (error) {
    console.error('Error creating requester contact:', error);
    // Don't throw - this is auto-sync, not user-initiated
  }
}

/**
 * Reject a contact request
 * @param {string} requestId
 */
export async function rejectContactRequest(requestId) {
  try {
    await updateDoc(doc(db, 'contactRequests', requestId), {
      status: 'rejected',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error rejecting contact request:', error);
    throw error;
  }
}

/**
 * Get a contact relationship (or null if doesn't exist)
 * @param {string} userId
 * @param {string} contactId
 * @returns {Promise<Contact|null>}
 */
export async function getContact(userId, contactId) {
  try {
    const contactId_key = `${userId}_${contactId}`;
    const doc_ = await getDoc(doc(db, 'contacts', contactId_key));
    return doc_.exists() ? doc_.data() : null;
  } catch (error) {
    // If it's a permission error on a non-existent document, return null
    // Only log other errors
    if (error.code !== 'permission-denied') {
      console.error('Error getting contact:', error);
    }
    return null;
  }
}

/**
 * Block a user (prevents all DMs and hides chats)
 * @param {string} blockerId - User doing the blocking
 * @param {string} blockedId - User being blocked
 */
export async function blockUser(blockerId, blockedId) {
  try {
    const blockId = `${blockerId}_${blockedId}`;

    // Get existing contact to preserve nickname/notes
    const existingContact = await getContact(blockerId, blockedId);

    const batch = writeBatch(db);

    // Create block record
    batch.set(doc(db, 'blockedUsers', blockId), {
      blockerId,
      blockedId,
      createdAt: serverTimestamp(),
      reason: ''
    });

    // Mark contact as blocked (preserve nickname/notes)
    batch.set(doc(db, 'contacts', `${blockerId}_${blockedId}`), {
      userId: blockerId,
      contactId: blockedId,
      status: 'blocked',
      createdAt: existingContact?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      contactName: existingContact?.contactName || '',
      sharedMeetingsCount: existingContact?.sharedMeetingsCount || 0,
      nickname: existingContact?.nickname || '',
      notes: existingContact?.notes || ''
    });

    await batch.commit();
  } catch (error) {
    console.error('Error blocking user:', error);
    throw error;
  }
}

/**
 * Unblock a user
 * @param {string} blockerId
 * @param {string} blockedId
 */
export async function unblockUser(blockerId, blockedId) {
  try {
    const blockId = `${blockerId}_${blockedId}`;

    const batch = writeBatch(db);

    batch.delete(doc(db, 'blockedUsers', blockId));
    batch.delete(doc(db, 'contacts', `${blockerId}_${blockedId}`));

    await batch.commit();
  } catch (error) {
    console.error('Error unblocking user:', error);
    throw error;
  }
}

/**
 * Archive a contact (hide but preserve)
 * @param {string} userId
 * @param {string} contactId
 */
export async function archiveContact(userId, contactId) {
  try {
    await updateDoc(doc(db, 'contacts', `${userId}_${contactId}`), {
      status: 'archived',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error archiving contact:', error);
    throw error;
  }
}

/**
 * Get all contacts for a user
 * @param {string} userId
 * @param {string} status - Filter by status (optional)
 * @returns {Promise<Contact[]>}
 */
export async function getUserContacts(userId, status = 'accepted') {
  try {
    const q = query(
      collection(db, 'contacts'),
      where('userId', '==', userId),
      where('status', '==', status)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user contacts:', error);
    return [];
  }
}

/**
 * Get pending contact requests FOR a user (incoming requests)
 * @param {string} userId
 * @returns {Promise<ContactRequest[]>}
 */
export async function getPendingRequests(userId) {
  try {
    const q = query(
      collection(db, 'contactRequests'),
      where('toUserId', '==', userId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting pending requests:', error);
    return [];
  }
}

/**
 * Get outgoing pending requests FROM a user (sent by user)
 * @param {string} userId
 * @returns {Promise<ContactRequest[]>}
 */
export async function getOutgoingPendingRequests(userId) {
  try {
    const q = query(
      collection(db, 'contactRequests'),
      where('fromUserId', '==', userId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting outgoing pending requests:', error);
    return [];
  }
}

/**
 * Check if there's a pending request between two users (either direction)
 * @param {string} userId1
 * @param {string} userId2
 * @returns {Promise<ContactRequest|null>}
 */
export async function getPendingRequestBetweenUsers(userId1, userId2) {
  try {
    // Check both possible request IDs
    const requestId1 = `${userId1}_${userId2}`;
    const requestId2 = `${userId2}_${userId1}`;

    const [doc1, doc2] = await Promise.all([
      getDoc(doc(db, 'contactRequests', requestId1)),
      getDoc(doc(db, 'contactRequests', requestId2))
    ]);

    if (doc1.exists() && doc1.data().status === 'pending') {
      return { id: doc1.id, ...doc1.data() };
    }
    if (doc2.exists() && doc2.data().status === 'pending') {
      return { id: doc2.id, ...doc2.data() };
    }

    return null;
  } catch (error) {
    console.error('Error checking pending request between users:', error);
    return null;
  }
}

/**
 * Get recent outgoing requests for rate limiting
 * @param {string} userId
 * @param {number} minutesAgo - How far back to look
 * @returns {Promise<ContactRequest[]>}
 */
export async function getRecentOutgoingRequests(userId, minutesAgo = 60) {
  try {
    const cutoffTime = new Date(Date.now() - minutesAgo * 60 * 1000);

    const q = query(
      collection(db, 'contactRequests'),
      where('fromUserId', '==', userId),
      where('createdAt', '>=', cutoffTime)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting recent outgoing requests:', error);
    return [];
  }
}

/**
 * Cancel an outgoing contact request
 * @param {string} requestId
 * @param {string} userId - Must be the sender
 */
export async function cancelContactRequest(requestId, userId) {
  try {
    const requestDoc = await getDoc(doc(db, 'contactRequests', requestId));
    if (!requestDoc.exists()) throw new Error('Request not found');

    const requestData = requestDoc.data();
    if (requestData.fromUserId !== userId) {
      throw new Error('Only the sender can cancel this request');
    }
    if (requestData.status !== 'pending') {
      throw new Error('Can only cancel pending requests');
    }

    await deleteDoc(doc(db, 'contactRequests', requestId));
  } catch (error) {
    console.error('Error canceling contact request:', error);
    throw error;
  }
}

/**
 * Check if user can DM another user
 * @param {string} userId - User initiating DM
 * @param {string} targetUserId - Target user
 * @returns {Promise<boolean>}
 */
export async function canDM(userId, targetUserId) {
  try {
    // Check if blocked
    const blockId = `${userId}_${targetUserId}`;
    const blocked = await getDoc(doc(db, 'blockedUsers', blockId));
    if (blocked.exists()) return false;

    // Check if contacts
    const contact = await getContact(userId, targetUserId);
    if (contact && contact.status === 'accepted') return true;

    // Check if same org
    const userOrg = await getDoc(doc(db, 'users', userId));
    const targetOrg = await getDoc(doc(db, 'users', targetUserId));

    return (
      userOrg.data()?.organizationId &&
      userOrg.data().organizationId === targetOrg.data()?.organizationId
    );
  } catch (error) {
    console.error('Error checking canDM:', error);
    return false;
  }
}

/**
 * Create contact from org membership
 * When user joins org, create contacts with all org members
 * @param {string} userId - New member
 * @param {string} orgId
 * @param {string} userName
 * @param {string} userPhotoURL
 */
export async function createOrgContacts(userId, orgId, userName, userPhotoURL) {
  try {
    // Get all org members
    const membersSnapshot = await getDocs(
      query(
        collection(db, 'organizations', orgId, 'members'),
        where('status', '==', 'active')
      )
    );

    const batch = writeBatch(db);

    for (const memberDoc of membersSnapshot.docs) {
      const memberId = memberDoc.id;
      if (memberId === userId) continue;

      const memberData = memberDoc.data();

      // Create contact for new member → existing member
      batch.set(doc(db, 'contacts', `${userId}_${memberId}`), {
        userId,
        contactId: memberId,
        status: 'accepted',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        contactName: memberData.name || 'Anonymous',
        contactPhotoURL: memberData.photoURL || null,
        sharedOrgId: orgId,
        sharedMeetingsCount: 0,
        nickname: '',
        notes: ''
      });

      // Create contact for existing member → new member
      batch.set(doc(db, 'contacts', `${memberId}_${userId}`), {
        userId: memberId,
        contactId: userId,
        status: 'accepted',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        contactName: userName || 'Anonymous',
        contactPhotoURL: userPhotoURL || null,
        sharedOrgId: orgId,
        sharedMeetingsCount: 0,
        nickname: '',
        notes: ''
      });
    }

    if (membersSnapshot.docs.length > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.error('Error creating org contacts:', error);
    throw error;
  }
}

/**
 * Remove org contacts when user leaves org
 * @param {string} userId
 * @param {string} orgId
 */
export async function removeOrgContacts(userId, orgId) {
  try {
    // Get all contacts with this org
    const q = query(
      collection(db, 'contacts'),
      where('userId', '==', userId),
      where('sharedOrgId', '==', orgId)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    for (const doc_ of snapshot.docs) {
      batch.delete(doc_.ref);
    }

    if (snapshot.docs.length > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.error('Error removing org contacts:', error);
    throw error;
  }
}

/**
 * Auto-create contact from shared meeting
 * When users share a meeting, suggest contact request
 * @param {string} userId1
 * @param {string} userId2
 * @param {string} meetingId
 */
export async function suggestContactFromMeeting(userId1, userId2, meetingId) {
  try {
    const existing = await getContact(userId1, userId2);
    if (existing) return; // Already contacts

    // Don't auto-create, just increment shared meetings count
    // Users must accept a request to become contacts
    // (Will implement contact request UI)
  } catch (error) {
    console.error('Error suggesting contact:', error);
  }
}

/**
 * Delete a contact permanently (bidirectional)
 * @param {string} userId - User deleting the contact
 * @param {string} contactId - Contact being deleted
 */
export async function deleteContact(userId, contactId) {
  try {
    const batch = writeBatch(db);

    // Delete contact for user
    batch.delete(doc(db, 'contacts', `${userId}_${contactId}`));

    // Delete contact for other user (bidirectional)
    batch.delete(doc(db, 'contacts', `${contactId}_${userId}`));

    // Clean up pending contact requests if any
    const requestsQuery = query(
      collection(db, 'contactRequests'),
      where('fromUserId', '==', userId),
      where('toUserId', '==', contactId)
    );
    const requests1 = await getDocs(requestsQuery);
    requests1.docs.forEach(doc_ => batch.delete(doc_.ref));

    // Clean up reverse requests
    const reverseRequestsQuery = query(
      collection(db, 'contactRequests'),
      where('fromUserId', '==', contactId),
      where('toUserId', '==', userId)
    );
    const requests2 = await getDocs(reverseRequestsQuery);
    requests2.docs.forEach(doc_ => batch.delete(doc_.ref));

    await batch.commit();
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
}

/**
 * Update contact details (nickname and notes)
 * @param {string} userId - User updating the contact
 * @param {string} contactId - Contact being updated
 * @param {Object} updates - { nickname?: string, notes?: string }
 * @returns {Promise<Object>} Updated contact object
 */
export async function updateContactDetails(userId, contactId, updates) {
  try {
    // Validate input
    if (updates.nickname && updates.nickname.length > 50) {
      throw new Error('Nickname must be 50 characters or less');
    }
    if (updates.notes && updates.notes.length > 500) {
      throw new Error('Notes must be 500 characters or less');
    }

    const contactRef = doc(db, 'contacts', `${userId}_${contactId}`);

    // Prepare update object with only provided fields
    const updateData = {
      updatedAt: serverTimestamp()
    };

    if ('nickname' in updates) {
      updateData.nickname = updates.nickname || '';
    }
    if ('notes' in updates) {
      updateData.notes = updates.notes || '';
    }

    await updateDoc(contactRef, updateData);

    // Return updated contact
    const updatedDoc = await getDoc(contactRef);
    return {
      id: updatedDoc.id,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error('Error updating contact details:', error);
    throw error;
  }
}
